/* RESPONSIBLE TEAM: team-frontend-tech */

/*
 * ⚠️  WARNING: BRIDGE LOGIC - DO NOT MODIFY WITHOUT #team-frontend-tech APPROVAL ⚠️
 *
 * This modifier contains critical bridge logic that enables communication between the
 * Ember app (apps/embercom) and React app (apps/teammate-app). Any modifications
 * to this modifier can break the integration between the two applications.
 *
 * Before making any changes to this file, please reach out to #team-frontend-tech
 * for review and approval.
 */

/* eslint-disable promise/prefer-await-to-then */
import Modifier from 'ember-modifier';
import { inject as service } from '@ember/service';
import type RouterService from '@ember/routing/router-service';
import { registerDestructor } from '@ember/destroyable';
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';
import ENV from 'embercom/config/environment';
import { isReactRoute } from 'embercom/lib/react-route';
import { loadScript, loadStylesheet } from 'embercom/lib/dom-utils';
import { getTeammateAppUrls, getViteBaseUrl } from 'embercom/lib/react-islands';

const CONTAINER_ID = 'react-teammate-app-island-container';

interface Args {
  Positional: [];
  Named: { onAppReady: () => void; onError: (error: Error) => void };
}

interface RenderReactTeammateAppIslandSignature {
  Element: Element;
  Args: Args;
}

class RenderReactTeammateAppIsland extends Modifier<RenderReactTeammateAppIslandSignature> {
  @service declare reactTeammateAppIsland: ReactTeammateAppIslandService;
  @service declare router: RouterService;

  hasShadowRoot = false;

  unmount?: Function;

  constructor(owner: any, args: any) {
    super(owner, args);

    registerDestructor(this, this.cleanup);
  }

  modify(element: Element, _props: Args['Positional'], { onAppReady, onError }: Args['Named']) {
    this.renderReactApp(element)
      .then(() => {
        onAppReady?.();
      })
      .catch((error) => {
        console.error(error);
        onError?.(error);
      });
  }

  cleanup = () => {
    this.unmount?.();
  };

  async renderReactApp(element: Element) {
    if (this.hasShadowRoot) {
      return;
    }

    if (ENV.environment === 'test') {
      return;
    }

    if (ENV.environment === 'development') {
      if (!this.reactTeammateAppIsland.loadedViteScripts) {
        let viteBaseUrl = getViteBaseUrl();
        await Promise.all([
          loadScript(`${viteBaseUrl}/react-refresh-preamble.js`),
          loadScript(`${viteBaseUrl}/@vite/client`),
        ]);

        this.reactTeammateAppIsland.loadedViteScripts = true;
      }
    }

    let { jsUrl, cssUrl } = getTeammateAppUrls();

    if (!this.reactTeammateAppIsland.loadedEntryPointScript) {
      await loadScript(jsUrl);

      this.reactTeammateAppIsland.loadedEntryPointScript = true;
    }

    if (!window.ReactTeammateApp) {
      throw new Error('ReactTeammateApp not found after loading script');
    }

    let reactAppMountElement = document.createElement('div');
    reactAppMountElement.id = CONTAINER_ID;
    reactAppMountElement.className = 'h-full w-full';

    let shadowRoot = element.attachShadow({ mode: 'open' });
    this.hasShadowRoot = true;

    await loadStylesheet(cssUrl, shadowRoot);
    shadowRoot.appendChild(reactAppMountElement);

    this.unmount = window.ReactTeammateApp.render(reactAppMountElement, {
      currentPath: this.reactPath,
    });
  }

  get reactPath() {
    return isReactRoute(this.router.currentRoute) ? this.router.currentURL : '/empty';
  }
}

export default RenderReactTeammateAppIsland;

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'render-react-teammate-app-island': typeof RenderReactTeammateAppIsland;
  }
}
